import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GroupDto, GroupQueryParams } from '../types/dtos/group';
import { groupService } from '../services/group.service';

const GROUPS_KEY = 'groups';

export const useGroups = (organizationId: string, params?: GroupQueryParams) => {
  return useQuery({
    queryKey: [GROUPS_KEY, organizationId, params],
    queryFn: async () => {
      const response = await groupService.getGroups(organizationId, params);
      // The response.data is already the array of groups
      return response.data;
    },
    enabled: !!organizationId
  });
};

export const useGroup = (organizationId: string, id: string) => {
  return useQuery({
    queryKey: [GROUPS_KEY, organizationId, id],
    queryFn: () => groupService.getGroupById(organizationId, id).then((res) => res.data),
    enabled: !!organizationId && !!id
  });
};

export const useCreateGroup = (organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (group: GroupDto) => groupService.createGroup(organizationId, group).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY, organizationId] });
    }
  });
};

export const useUpdateGroup = (organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, group }: { id: string; group: GroupDto }) =>
      groupService.updateGroup(organizationId, id, group).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY, organizationId] });
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY, organizationId, id] });
    }
  });
};

export const useDeleteGroup = (organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => groupService.deleteGroup(organizationId, id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY, organizationId] });
    }
  });
}; 